import { TezosToolkit } from '@taquito/taquito';
import { BeaconWallet } from '@taquito/beacon-wallet';
import { NetworkType } from '@airgap/beacon-types';

export type NetworkOption = 'sandbox' | 'ghostnet' | 'mainnet';

// Sandbox RPC only available in development - use PROD check for proper dead code elimination
const SANDBOX_RPC = import.meta.env.PROD ? 'https://mainnet.api.tez.ie' : 'http://localhost:20000';

export const NETWORK_CONFIG: Record<NetworkOption, { type: NetworkType; rpcUrl: string }> = {
  sandbox: { type: NetworkType.CUSTOM, rpcUrl: SANDBOX_RPC },
  // Use teztnets RPC - has CORS enabled (marigold.dev does not)
  ghostnet: { type: NetworkType.GHOSTNET, rpcUrl: 'https://rpc.ghostnet.teztnets.com' },
  mainnet: { type: NetworkType.MAINNET, rpcUrl: 'https://mainnet.api.tez.ie' },
};

// getRpcUrl returns the RPC URL based on environment
// In production, this is always mainnet or ghostnet
// In development, this can be sandbox (localhost)
export const getRpcUrl = (): string => {
  const rpcUrl = import.meta.env.VITE_RPC_URL;
  if (rpcUrl) return rpcUrl;

  const network = import.meta.env.VITE_NETWORK;
  if (network === 'mainnet') return 'https://mainnet.api.tez.ie';
  // Use teztnets RPC - has CORS enabled (marigold.dev does not)
  if (network === 'ghostnet') return 'https://rpc.ghostnet.teztnets.com';
  // Default to mainnet for production, sandbox for development
  return import.meta.env.PROD ? 'https://mainnet.api.tez.ie' : 'http://localhost:20000';
};

export const createTezosToolkit = (rpcUrl?: string) => {
  return new TezosToolkit(rpcUrl || getRpcUrl());
};

// Wallet instance - recreated when switching networks
let walletInstance: BeaconWallet | null = null;
let currentNetworkType: NetworkOption | null = null;

export const createBeaconWallet = (network?: NetworkOption) => {
  const targetNetwork = network || (import.meta.env.VITE_NETWORK as NetworkOption) || 'ghostnet';
  const config = NETWORK_CONFIG[targetNetwork];

  // Return existing instance if same network
  if (walletInstance && currentNetworkType === targetNetwork) {
    return walletInstance;
  }

  // Create new wallet with target network
  console.log('[createBeaconWallet] Creating wallet for network:', targetNetwork);
  console.log('[createBeaconWallet] NetworkType value:', config.type);

  walletInstance = new BeaconWallet({
    name: 'Tezos Marketplace',
    network: {
      type: config.type,
      rpcUrl: config.rpcUrl,
    },
  });

  // Force set the network on the client since BeaconWallet doesn't pass it correctly
  (walletInstance.client as any).network = { type: config.type, rpcUrl: config.rpcUrl };

  // Debug: log the actual network the client is using
  console.log('[createBeaconWallet] Wallet client network after override:', (walletInstance.client as any).network);

  currentNetworkType = targetNetwork;
  return walletInstance;
};

export const getBeaconWallet = () => walletInstance;

export const resetWalletForNetwork = async (network: NetworkOption) => {
  // Properly clean up existing wallet
  if (walletInstance) {
    try {
      await walletInstance.clearActiveAccount();
    } catch (e) {
      // Ignore cleanup errors
    }
    walletInstance = null;
    currentNetworkType = null;
  }

  // Create fresh wallet for new network
  return createBeaconWallet(network);
};

export const destroyBeaconWallet = async () => {
  if (walletInstance) {
    await walletInstance.disconnect();
    walletInstance = null;
  }
};

export const getMarketplaceContract = async (tezos: TezosToolkit) => {
  const contractAddress = import.meta.env.VITE_MARKETPLACE_CONTRACT;
  if (!contractAddress) {
    throw new Error('Marketplace contract address not configured');
  }
  return await tezos.contract.at(contractAddress);
};
